import { cn } from "~/lib/cn";
import { Button } from "~/components/ui/button";
import { $, Slot, component$ } from "@builder.io/qwik";

import type { HTMLAttributes } from "@builder.io/qwik";
import { useNavigate } from "@builder.io/qwik-city";

interface FormStepProgressProps {
  totalSteps: number;
  currentIndex: number;
}

const FormStepProgress = component$(
  ({ totalSteps, currentIndex }: FormStepProgressProps) => {
    const nav = useNavigate();

    return (
      <div class="flex items-center gap-x-2">
        {Array.from({ length: totalSteps }, (_, index) => (
          <div
            key={index}
            onClick$={
              index < currentIndex + 1
                ? $(() => nav(`/discovery/steps/${index + 1}`))
                : undefined
            }
            class={cn(
              "h-2 flex-1 rounded",
              index < currentIndex + 1 ? "bg-brand-400" : "bg-brand-100",
              {
                "hover:bg-brand-500 cursor-pointer transition":
                  index < currentIndex + 1,
              },
            )}
          ></div>
        ))}
      </div>
    );
  },
);

const FormStepCard = component$(
  (props: HTMLAttributes<HTMLDivElement> & { error?: unknown }) => {
    const { class: className, ...rest } = props;
    return (
      <div
        class={cn(
          "bg-gray-25 mt-6 rounded-2xl border border-gray-100 p-8",
          {
            "border-red-700": props.error,
          },
          className,
        )}
        {...rest}
      >
        <Slot />
      </div>
    );
  },
);

interface FormStepControlProps {
  next?: () => void;
  prev?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
}

const FormStepControl = component$((props: FormStepControlProps) => {
  return (
    <div
      class={cn("mt-6 flex justify-center", {
        "justify-between": props.prev,
      })}
    >
      {props.prev && (
        <Button type="button" onClick$={props.prev} variant="ghost">
          Back
        </Button>
      )}
      <Button
        type={props.type}
        onClick$={props.next}
        disabled={props.disabled}
        loading={props.loading}
        variant="default"
      >
        Next
      </Button>
    </div>
  );
});

interface FormStepContentProps {
  stepPosition: number;
}

const FormStepContent = component$(({ stepPosition }: FormStepContentProps) => {
  return (
    <div class="relative mx-auto w-full max-w-2xl overflow-x-hidden">
      <div
        class="flex transition-transform duration-300 ease-in-out"
        style={`transform: translateX(-${stepPosition * 100}%);`}
      >
        <Slot />
      </div>
    </div>
  );
});

interface FormStepTitleProps {
  title?: string;
}

const FormStepTitle = component$(({ title }: FormStepTitleProps) => {
  return (
    <h1 class="font-heading text-primary mt-12 px-4 text-center text-3xl font-medium tracking-tight">
      {title}
    </h1>
  );
});
const FormStepSubTitle = component$(({ title }: FormStepTitleProps) => {
  return <h3 class="text-secondary mt-4 px-8 text-center text-lg">{title}</h3>;
});

export const FormStep = {
  Card: FormStepCard,
  Title: FormStepTitle,
  SubTitle: FormStepSubTitle,
  Control: FormStepControl,
  Content: FormStepContent,
  Progress: FormStepProgress,
};
